import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MyDataConnectLayout from '../components/mydata/MyDataConnectLayout';
import { getMyDataDraft } from '../utils/myDataDraft';
import { authHeader } from '../utils/auth';

export default function MyDataAnalysisReadyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/app') ? '/app' : '';
  const [isSubmittingAnalysis, setIsSubmittingAnalysis] = useState(false);

  const tone = {
    surface: '#ffffff',
    surfaceMuted: '#f9fafb',
    border: '#e5e7eb',
    borderSoft: '#d1d5db',
    textStrong: '#111827',
    text: '#374151',
    textMuted: '#6b7280',
    primary: '#3498db',
    success: '#16a34a',
    successSoft: '#f0fdf4',
  };

  useEffect(() => {
    const draft = getMyDataDraft();
    const phases = ['consent', 'guide', 'fetching', 'analysis-ready'];
    if (!draft || !draft.phase || phases.indexOf(draft.phase) < phases.indexOf('analysis-ready')) {
      navigate(basePath + '/mydata/connect/consent', { replace: true });
    }
  }, [basePath, navigate]);

  const dashboardDest = basePath ? basePath + '/analysis/dashboard' : '/analysis/running';

  const handleAnalysisNow = async () => {
    setIsSubmittingAnalysis(true);
    const draft = getMyDataDraft();
    const apiUrl = (process.env.REACT_APP_API_URL ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '') : '');
    let submitted = false;
    const isInAppWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;

    if (!isInAppWebView && basePath !== '/app') {
      navigate(dashboardDest, {
        replace: true,
        state: { stdPatientId: draft?.stdPatientId ?? null },
      });
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/api/v1/analysis/submit-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ std_patient_id: draft?.stdPatientId ?? null, source: isInAppWebView ? 'app' : 'web' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '분석 요청에 실패했습니다.');
      submitted = true;
    } catch (err) {
      console.error('[MyDataAnalysisReady] submit-direct 오류:', err);
    } finally {
      navigate(dashboardDest, {
        replace: true,
        state: submitted
          ? { analysisStartedToast: true, analysisStartedAt: Date.now(), stdPatientId: draft?.stdPatientId ?? null }
          : undefined,
      });
    }
  };

  return (
    <MyDataConnectLayout step={2}>
      <div style={{ textAlign: 'center', padding: '8px 0 28px' }}>
        {basePath !== '/app' && (
          <h1 className="page-title" style={{ marginBottom: 6, fontSize: 24 }}>
            이제 건강 리포트를 만들 차례예요
          </h1>
        )}
        <p className="page-description" style={{ marginBottom: 24 }}>
          아래 내용을 확인하고 바로 시작할 수 있어요.
        </p>

        <div
          style={{
            background: tone.successSoft,
            border: `1px solid #bbf7d0`,
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 12,
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: tone.success,
                flexShrink: 0,
              }}
            />
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#166534' }}>
              의료 데이터 수집이 무사히 끝났어요
            </p>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: tone.text, lineHeight: 1.5 }}>
            건강검진, 진료, 투약 정보가 모두 준비되어 있어 바로 분석을 시작할 수 있어요.
          </p>
        </div>

        <div
          style={{
            background: tone.surfaceMuted,
            border: `1px solid ${tone.border}`,
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 12,
            textAlign: 'left',
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: tone.textStrong }}>
            분석 대상 데이터
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['건강검진결과', '진료내역', '투약정보'].map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 12,
                  padding: '3px 10px',
                  borderRadius: 20,
                  background: tone.surface,
                  color: tone.text,
                  fontWeight: 600,
                  border: `1px solid ${tone.border}`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div
          style={{
            background: tone.surfaceMuted,
            border: `1px solid ${tone.border}`,
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 24,
            textAlign: 'left',
          }}
        >
          <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: tone.textStrong }}>
            이렇게 진행돼요
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            {[
              {
                step: 1,
                title: '검진 요약 먼저 확인',
                description: '분석을 시작하면 바로 검진 요약 화면으로 이동해요. 수집한 건강검진, 진료, 투약 정보를 먼저 한눈에 볼 수 있어요.',
              },
              {
                step: 2,
                title: '리포트가 준비되면 알림 도착',
                description: 'AI 건강 리포트는 보통 3~5분 정도 걸리고, 준비가 끝나면 푸시 알림으로 알려드릴게요.',
              },
            ].map((item, index, arr) => (
              <div
                key={item.step}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '24px 1fr',
                  gap: 12,
                  alignItems: 'start',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: tone.surface,
                      border: `1px solid ${tone.borderSoft}`,
                      color: tone.primary,
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {item.step}
                  </span>
                  {index < arr.length - 1 && (
                    <span
                      style={{
                        width: 1,
                        flex: 1,
                        minHeight: 28,
                        marginTop: 6,
                        background: tone.borderSoft,
                      }}
                    />
                  )}
                </div>
                <div style={{ paddingTop: 1 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: tone.textStrong }}>
                    {item.title}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: tone.textMuted, lineHeight: 1.55 }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: `1px dashed ${tone.borderSoft}`,
            }}
          >
            <p style={{ margin: 0, fontSize: 12, color: tone.textMuted, lineHeight: 1.5 }}>
              앱을 닫아도 분석은 계속 진행돼요.
            </p>
          </div>
        </div>

        <button
          type="button"
          disabled={isSubmittingAnalysis}
          aria-busy={isSubmittingAnalysis}
          aria-live="polite"
          onClick={handleAnalysisNow}
          style={{
            width: '100%',
            padding: '15px 0',
            borderRadius: 12,
            border: 'none',
            cursor: isSubmittingAnalysis ? 'not-allowed' : 'pointer',
            background: isSubmittingAnalysis ? '#9ca3af' : tone.primary,
            color: '#fff',
            fontSize: 16,
            fontWeight: 700,
            transition: 'opacity 0.2s',
          }}
        >
          {isSubmittingAnalysis ? '리포트 준비 중...' : '이제 건강 리포트 만들어볼까요'}
        </button>
      </div>
    </MyDataConnectLayout>
  );
}
